import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const AuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const checkSubscriptionAndRedirect = useCallback(async () => {
    try {
      const response = await api.get('/subscriptions/status/my-subscription');
      const subscription = response.data;
      
      // Check if user has an active subscription
      if (subscription && subscription.isActive && !subscription.isExpired) {
        // User has active subscription, go to learning dashboard
        navigate('/learning');
      } else {
        // No active subscription, redirect to pricing
        navigate('/pricing');
      }
    } catch (error) {
      console.error('Error checking subscription status:', error);
      // If check fails, redirect to pricing
      navigate('/pricing');
    }
  }, [navigate]);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Check if we have the necessary parameters from the OAuth callback
        const token = searchParams.get('token');
        const user = searchParams.get('user');
        const error = searchParams.get('error');

        if (error) {
          setError('Authentication failed. Please try again.');
          setLoading(false);
          return;
        }

        if (token && user) {
          // Store the tokens and user data
          localStorage.setItem('access_token', token);
          localStorage.setItem('user', user);
          
          // Parse user data and update context
          const userData = JSON.parse(decodeURIComponent(user));
          setUser(userData);

          // Check subscription status and redirect accordingly
          await checkSubscriptionAndRedirect();
        } else {
          // If no token, check if we're in a popup and need to handle the response differently
          // This handles the case where the backend redirects back with user data
          const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/status`, {
            credentials: 'include'
          });

          if (response.ok) {
            const data = await response.json();
            if (data.user && data.access_token) {
              localStorage.setItem('access_token', data.access_token);
              localStorage.setItem('refresh_token', data.refresh_token || '');
              localStorage.setItem('user', JSON.stringify(data.user));
              
              setUser(data.user);
              
              // Check subscription status and redirect accordingly
              await checkSubscriptionAndRedirect();
            } else {
              setError('Authentication incomplete. Please try again.');
            }
          } else {
            setError('Authentication failed. Please try again.');
          }
        }
      } catch (err) {
        console.error('Auth callback error:', err);
        setError('An error occurred during authentication. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    handleCallback();
  }, [searchParams, navigate, setUser, checkSubscriptionAndRedirect]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Completing authentication...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center max-w-md mx-auto p-6 bg-white rounded-lg shadow-lg">
          <div className="text-red-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Authentication Failed</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition-colors"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default AuthCallback;
